import axios from "axios";

const API_BASE_URL = import.meta.env.PROD
  ? "/api"
  : import.meta.env.VITE_API_BASE_URL || "http://localhost:2520/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add timestamp to prevent caching issues
    if (config.method === "get") {
      config.params = {
        ...config.params,
        _t: Date.now(),
      };
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?session_expired=true';
      }
    }
    return Promise.reject(error);
  }
);

export const invoiceService = {
  getAll: (params = {}) => api.get("/invoices", { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (payload) => api.post("/invoices", payload),
  update: (id, payload) => api.put(`/invoices/${id}`, payload),
  replaceLineItems: (id, lineItems) => api.put(`/invoices/${id}/line-items`, lineItems),
};

export const componentOrderService = {
  getAll: (filters = {}) => api.get("/componentOrders", { params: filters }),
  getRecent: () => api.get(`/componentOrders/recent`),
  getDashboardSummary: () => api.get("/componentOrders/dashboard-summary"),
  getById: (id) => api.get(`/componentOrders/${id}`),
  getHistory: (id) => api.get(`/componentOrders/${id}/history`),
  create: (componentOrderData) => api.post("/componentOrders", componentOrderData),
  update: (id, componentOrderData) => api.put(`/componentOrders/${id}`, componentOrderData),
  delete: (id) => api.delete(`/componentOrders/${id}`),
};

export const partService = {
  getAll: (params = {}) => api.get("/parts", { params }),
  getById: (id) => api.get(`/parts/${id}`),
  getHistory: (id) => api.get(`/parts/${id}/history`),
  create: (payload) => api.post("/parts", payload),
  update: (id, payload) => api.put(`/parts/${id}`, payload),
};

export const orderUsedPartsService = {
  list: (orderId) => api.get(`/componentOrders/${orderId}/used-parts`),
  create: (orderId, payload) => api.post(`/componentOrders/${orderId}/used-parts`, payload),
  update: (orderId, lineId, payload) =>
    api.patch(`/componentOrders/${orderId}/used-parts/${lineId}`, payload),
  delete: (orderId, lineId) => api.delete(`/componentOrders/${orderId}/used-parts/${lineId}`),
};

export const countryService = {
  getAll: () => api.get("/countries"),
};

// Auth service
export const authService = {
  login: (credentials) => api.post("/auth/login", credentials),
  register: (userData) => api.post("/auth/register", userData),
  logout: () => api.post("/auth/logout"),
  getProfile: () => api.get("/users/profile"),
  getPermissions: () => api.get("/auth/permissions"),
  getRoleInfo: () => api.get("/auth/role-info"),
  getRoles: () => api.get("/auth/roles"),
  checkPermission: (permission) => api.post("/auth/check-permission", { permission }),
  updateProfile: (userData) => api.put("/users/profile", userData),
  changePassword: (passwordData) => api.put("/users/change-password", passwordData),
};

// User service
export const userService = {
  getProfile: () => api.get("/users/profile"),
  getAllUsers: () => api.get("/users"),
  updateProfile: (userData) => api.put("/users/profile", userData),
};

export default api;
